"use client";

import { useEffect } from "react";
import { useStudio } from "@/contexts/studio-context";
import type { StudioContextType } from "@/lib/analytics";
import type { MidiController } from "@/lib/piano/input/controllers/midi-controller";
import {
  endStudioSession,
  recordCountIn,
  recordNotePlayed,
  recordPause,
  recordSeek,
  recordTransportStarted,
  startStudioSession,
} from "@/lib/analytics/studio-session";

/**
 * Branche l'enveloppe de session du studio sur les coutures du runtime.
 *
 * # Pourquoi un hook et pas des appels dans la page
 *
 * Les gestes à compter n'arrivent pas par React : ils arrivent par le
 * transport, par le bus du piano, par la vue Pixi. Il faut donc un endroit qui
 * s'abonne une fois pour toutes, et un seul — deux montages compteraient deux
 * fois chaque note. Ce hook n'est appelé que par `StudioPage`, qui est monté
 * une fois pour toute la section.
 *
 * # Ce qu'il ne fait pas
 *
 * Il ne décide de rien et ne connaît pas le moteur de rendu : toute la règle
 * de comptage vit dans `lib/analytics/studio-session`, qui se teste sans
 * React, et les coutures utilisées ici existaient déjà pour d'autres raisons.
 *
 * Il ne remplace pas non plus `exercise-session` ni les `practice_session_*`
 * du parcours : ceux-là décrivent l'apprentissage, celui-ci décrit le studio.
 * Un exercice produit bien les deux, et c'est voulu.
 */
export type UseStudioSessionOptions = {
  /**
   * Ce sur quoi porte la session, ou `null` quand le studio n'est pas l'écran.
   *
   * `StudioPage` reste monté sur toute la section `(main)`, seulement caché
   * hors des routes du studio : sans ce `null`, une demi-heure passée dans la
   * bibliothèque serait comptée comme une demi-heure de studio.
   */
  readonly context: StudioContextType | null;
  /**
   * Le morceau chargé, qui « possède » la session. `null` en jeu libre.
   *
   * Comparer les identités suffit à voir que le studio est passé à autre
   * chose, là où câbler une sortie sur chacun des chemins de chargement en
   * oublierait un tôt ou tard. Même patron que `exercise-session`.
   */
  readonly owner: object | null;
  /** Identité **catalogue** du morceau, jamais celle du fichier MIDI. */
  readonly pieceId?: string;
  readonly exerciseId?: string;
};

/**
 * Un clavier MIDI est-il branché **et** routé vers le piano en ce moment ?
 *
 * Le bus du piano ne dit pas de quel périphérique vient une note : il dit
 * seulement qu'elle vient de la personne et non de l'application. La question
 * à laquelle `midiUsed` répond n'est pas « cette note-ci est-elle arrivée par
 * le DIN » mais « y avait-il un vrai piano au bout pendant qu'on jouait », et
 * cette lecture-là y répond sans faire porter l'origine par le bus.
 */
function midiRouted(midi: MidiController | null): boolean {
  const devices = midi?.getInput()?.getDevices();

  if (devices === undefined) {
    return false;
  }

  return devices.some((device) => device.connected && device.enabled);
}

/**
 * La mesure ne casse jamais l'application.
 *
 * Ces écoutes sont appelées depuis les boucles d'émission du piano et du
 * transport, qui servent leurs auditeurs à la file : une exception échappée
 * d'ici priverait le rendu et l'audio de l'événement qu'ils attendaient.
 */
function guarded(record: () => void): void {
  try {
    record();
  } catch {
    // Une mesure perdue vaut mieux qu'un piano muet.
  }
}

export function useStudioSession({
  context,
  owner,
  pieceId,
  exerciseId,
}: UseStudioSessionOptions): void {
  const { studio, midiController, micController } = useStudio();

  // Ouverture, et bascule d'un sujet à l'autre. `startStudioSession` est
  // idempotent sur ces quatre valeurs : un effet qui rejoue ne coupe pas en
  // deux la session en cours.
  useEffect(() => {
    if (context === null) {
      endStudioSession("navigation");

      return;
    }

    startStudioSession(context, { owner, pieceId, exerciseId });
  }, [context, owner, pieceId, exerciseId]);

  // Le studio s'en va pour de bon.
  //
  // Le départ de la page — mise en arrière-plan comprise, qui est le sort
  // ordinaire d'un onglet sur la tablette du pupitre — est déjà tenu par le
  // module lui-même via `onDeparture`, avec le vidage de file et la
  // **réouverture** au retour au premier plan. En poser un second ici
  // fermerait deux fois la même enveloppe, et surtout sans rouvrir : on
  // troquerait la perte contre une troncature.
  //
  // Le remontage, lui, n'a besoin de rien : l'effet du dessus rejoue sur
  // `context` et rouvre la session.
  useEffect(() => {
    return () => {
      endStudioSession("navigation");
    };
  }, []);

  useEffect(() => {
    if (!studio) {
      return;
    }

    const localTransport = studio.getLocalTransport();

    const offTransport = localTransport.on((event) => {
      // Le départ du **son** se prend ici, et nulle part ailleurs : le
      // transport est la seule source qui voie les reprises menées par
      // l'application. `ViewNavigator.beginGesture()` met en pause le temps
      // d'un glissé du rouleau ou d'un clic de minimap puis relance au
      // `commit()` ; le retour de boucle relance après son décompte ; une
      // astuce de section aussi. Aucune ne passe par un bouton.
      //
      // Posé sur la commande de la barre, le chronomètre s'arrêtait donc pour
      // de bon au premier geste de travail : la pause encaissait le segment,
      // la reprise ne le rouvrait pas, et `playedMs` restait à zéro pour le
      // reste de la séance — d'autant plus faux que la personne travaillait.
      //
      // `plays` reste sur la commande, lui : c'est une **intention**, et ni le
      // jingle d'accueil ni la passe automatique d'un parcours ne sont
      // l'intention de personne. Voir `Toolbar` et `recordPlay`.
      if (event.type === "play") {
        guarded(recordTransportStarted);

        return;
      }

      // Toute mise à l'arrêt referme le segment de temps joué, quelle qu'en
      // soit la cause : fin de piste, retour de boucle, modale de compte.
      if (event.type === "detach") {
        guarded(recordPause);
      }
    });

    // Silencieux quand c'est l'application qui défile : ce que cette couture
    // rapporte est donc toujours un déplacement voulu — glissé du rouleau,
    // molette, ou clic dans la minimap, que la couche de vue ne distingue pas
    // et n'a pas à distinguer.
    const offScrub = studio.onViewScrub(() => {
      guarded(recordSeek);
    });

    const offCountIn = studio.countIn.on((event) => {
      // La première pastille, et elle seule : les suivantes sont le même
      // décompte. Le retour de boucle en arme un lui aussi, et il est compté
      // parce qu'il a sonné — `loops` est là pour le relire.
      if (event.type === "start") {
        guarded(recordCountIn);
      }
    });

    const offPiano = studio.getPiano().on((event) => {
      // `origin` est la couture qui sépare la personne de l'application. Sans
      // elle, une pré-écoute d'accord ou une passe de parcours gonflerait
      // `notesPlayed` de notes que personne n'a jouées.
      if (event.type !== "press" || event.origin !== "player") {
        return;
      }

      // Mais `origin` ne suffit pas quand le micro écoute : `MicrophoneInput`
      // pousse ce qu'il détecte par `piano.press(note, velocity)` sans passer
      // d'origine, donc sous la valeur par défaut « player », exactement
      // comme un doigt. Or il n'y a pas d'annulation d'écho : ce qu'il entend
      // pendant que l'application sonne, c'est le morceau lui-même sortant du
      // haut-parleur, et rien dans le bus ne permet de l'en distinguer.
      //
      // On ne compte donc rien tant que le micro capte et que le transport
      // tourne. Le coût est connu et assumé : la personne qui joue au micro
      // par-dessus la lecture perd ses notes. L'inverse était pire — une
      // écoute passive, micro ouvert, se serait présentée comme une séance de
      // jeu sur le compteur même qui sépare l'auditeur du pianiste.
      if (micController?.active.value === true && !localTransport.isDetached) {
        return;
      }

      guarded(() => recordNotePlayed(midiRouted(midiController)));
    });

    return () => {
      offTransport();
      offScrub();
      offCountIn();
      offPiano();
    };
  }, [studio, midiController, micController]);
}
